
import React from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { PieChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardPostsTable from "@/components/admin/DashboardPostsTable";
import AnalyticsCharts from "@/components/admin/AnalyticsCharts";
import { BlogPost } from "@/hooks/useBlogPosts";
import { AnalyticsData } from "@/hooks/useAnalytics";
import { useToolUsageStats } from "@/hooks/useToolUsageStats";

interface DashboardTabsProps {
  posts: BlogPost[];
  postsLoading: boolean;
  analyticsData: AnalyticsData | null;
  analyticsLoading: boolean;
  analyticsError: Error | null;
}

const DashboardTabs: React.FC<DashboardTabsProps> = ({
  posts,
  postsLoading,
  analyticsData,
  analyticsLoading,
  analyticsError
}) => {
  const { toolUsageStats, isLoading: isToolStatsLoading } = useToolUsageStats();

  // Helper function to get stats for a specific tool
  const getToolStats = (toolName: string) => {
    const stats = toolUsageStats.find(t => t.toolName === toolName);
    return {
      usageCount: stats?.totalUsage || 0,
      embedCount: stats?.postEmbeds || 0
    };
  };

  const craftStats = getToolStats('craftGenerator');
  const sceneStats = getToolStats('sceneGenerator');
  const recipeStats = getToolStats('recipeGenerator');

  return (
    <Tabs defaultValue="posts">
      <TabsList className="mb-4">
        <TabsTrigger value="posts">Blog Posts</TabsTrigger>
        <TabsTrigger value="analytics">Analytics</TabsTrigger>
        <TabsTrigger value="tools">Tools</TabsTrigger>
      </TabsList>
      
      <TabsContent value="posts" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Recent Blog Posts</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardPostsTable posts={posts} isLoading={postsLoading} />
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="analytics">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Analytics Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <div className="h-80 flex items-center justify-center">
                <p className="text-lg text-gray-500">Loading analytics data...</p>
              </div>
            ) : analyticsError ? (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center space-y-4">
                  <PieChart size={64} className="mx-auto text-elfGreen" />
                  <p className="text-lg text-gray-500">
                    Error loading analytics data. Please try again later.
                  </p>
                </div>
              </div>
            ) : !analyticsData ? (
              <div className="h-80 flex items-center justify-center">
                <p className="text-lg text-gray-500">No analytics data available.</p>
              </div>
            ) : (
              <AnalyticsCharts 
                monthlyData={analyticsData.monthlyData} 
                topPosts={analyticsData.topPosts}
              />
            )}
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="tools" className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">Interactive Tools</CardTitle>
              <Link to="/admin/tools">
                <Button size="sm" className="bg-elfGreen hover:bg-elfGreen-dark">
                  Manage Tools
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Craft Generator */}
              <div className="p-4 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium">Holiday Elf Craft Generator</h3>
                  <Link to="/admin/tools/craft-generator">
                    <Button variant="outline" size="sm">Configure</Button>
                  </Link>
                </div>
                <p className="text-gray-600 text-sm">
                  Interactive craft idea generator that suggests holiday crafts based on available supplies.
                </p>
                <div className="mt-2 flex gap-2">
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Embedded: {isToolStatsLoading ? '...' : craftStats.embedCount} times
                  </div>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Used: {isToolStatsLoading ? '...' : craftStats.usageCount} times
                  </div>
                </div>
              </div>

              {/* Scene Generator */}
              <div className="p-4 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium">Holiday Elf Scene Generator</h3>
                  <Link to="/admin/tools/scene-generator">
                    <Button variant="outline" size="sm">Configure</Button>
                  </Link>
                </div>
                <p className="text-gray-600 text-sm">
                  Interactive tool that generates creative elf scene ideas for setting up holiday magic.
                </p>
                <div className="mt-2 flex gap-2">
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Embedded: {isToolStatsLoading ? '...' : sceneStats.embedCount} times
                  </div>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Used: {isToolStatsLoading ? '...' : sceneStats.usageCount} times
                  </div>
                </div>
              </div>

              {/* Recipe Generator */}
              <div className="p-4 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium">Holiday Elf Recipe Generator</h3>
                  <Link to="/admin/tools/recipe-generator">
                    <Button variant="outline" size="sm">Configure</Button>
                  </Link>
                </div>
                <p className="text-gray-600 text-sm">
                  Interactive tool that generates festive holiday recipes for all occasions.
                </p>
                <div className="mt-2 flex gap-2">
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Embedded: {isToolStatsLoading ? '...' : recipeStats.embedCount} times
                  </div>
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Used: {isToolStatsLoading ? '...' : recipeStats.usageCount} times
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default DashboardTabs;
